import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Typography, Row, Col } from "antd";
import {
    IconStar,
    IconMapPin,
    IconClock,
    IconTruck,
    IconHeart,
    IconHeartFilled,
    IconPlus,
    IconMinus,
    IconShoppingCart
} from "@tabler/icons-react";

import { CartContext } from "../CartContext";
import { WishlistContext } from "../WishlistContext";
import { LocationContext } from "../LocationContext";
import AuthServ from "../service/AuthServ";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import GlobalButton from "../util/GlobalButton";
import CheckoutModal from "./CheckoutPage";
import SafeImage from "./SafeImage";
import ProductCard from "./ProductCard";
import { useDeliveryEstimateContext } from "../context/DeliveryEstimateContext";
import GlobalTooltip from "./GlobalTooltip";
import { capitalizeFirst, renderSafeText } from "../util/unitFormatter";

import "../css/shopProductRow.css";

const { Meta } = Card;

const ShopProductRow = ({ shop, products = [], maxProducts = 10, hideShopHeader = false }) => {
    const navigate = useNavigate();
    const { showAlert } = useGlobalAuthModal();

    const {
        cart = { items: [] },
        addToCart,
        updateQuantity,
        removeFromCart
    } = useContext(CartContext) || {};

    const {
        toggleWishlist,
        isInWishlist = () => false
    } = useContext(WishlistContext) || {};

    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [checkoutProduct, setCheckoutProduct] = useState(null);

    const { latitude, longitude } = useContext(LocationContext) || {};
    const shopIdForEstimate = shop._id || shop.id;

    // Use the shared app-wide context instead of one hook per ShopProductRow
    const { registerShop, formatETA } = useDeliveryEstimateContext();
    React.useEffect(() => {
        registerShop(shopIdForEstimate);
    }, [shopIdForEstimate, latitude, longitude]);

    const deliveryTimeDisplay = formatETA(
        shopIdForEstimate,
        renderSafeText(shop.business?.deliveryTime) || renderSafeText(shop.deliveryTime)
    );


    const displayProducts = products.slice(0, maxProducts);

    /* ---------------- SHOP & PRODUCT NAV ---------------- */
    const handleShopClick = () => {
        navigate(`/products/${shop._id}`);
    };

    const handleProductClick = (product) => {
        window.open(`/single/Products/${product._id}`, '_blank');
    };

    /* ---------------- CART HANDLERS ---------------- */
    const handleAddToCart = async (e, productId) => {
        e.stopPropagation();

        const product = products.find(p => (p.id || p._id) === productId);
        if (product) {
            await addToCart(productId, 1, {}, {
                name: product.name,
                image: product.imageUrl || (product.images && product.images[0]),
                price: product.price,
                mrp: product.mrp,
                shopId: product.shopId?._id || product.shopId || shop?._id || shop?.id
            });
        } else {
            await addToCart(productId, 1);
        }
    };

    const handleIncrease = async (e, productId, qty) => {
        e.stopPropagation();
        const cartItem = cart.items.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            await updateQuantity(cartItem._id, qty + 1);
        }
    };

    const handleDecrease = async (e, productId, qty) => {
        e.stopPropagation();
        const cartItem = cart.items.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            if (qty > 1) {
                await updateQuantity(cartItem._id, qty - 1);
            } else {
                await removeFromCart(cartItem._id);
            }
        }
    };

    /* ---------------- BUY NOW ---------------- */
    const handleBuyNow = (e, product) => {
        e.stopPropagation();

        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Authentication Required",
                description: "Please log in to proceed to checkout.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    {
                        text: "Login",
                        variant: "primary",
                        onClick: () => navigate("/customer-login")
                    }
                ]
            });
            return;
        }

        setCheckoutProduct(product);
        setCheckoutOpen(true);
    };

    /* ---------------- WISHLIST ---------------- */
    const handleWishlistToggle = async (e, productId) => {
        e.stopPropagation();

        if (!AuthServ.isLogged()) {
            showAlert({
                title: "Authentication Required",
                description: "Please log in to use the wishlist.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    {
                        text: "Login",
                        variant: "primary",
                        onClick: () => navigate("/customer-login")
                    }
                ]
            });
            return;
        }

        await toggleWishlist(productId);
    };

    return (
        <div className="shop-product-row">
            {/* ---------------- SHOP HEADER ---------------- */}
            {!hideShopHeader && (
                <div className="shop-row-header-box" onClick={handleShopClick}>
                    <div className="shop-row-name-container">
                        <h3 className="shop-row-name-text">{renderSafeText(shop.shopName)}</h3>
                    </div>
                    
                    <div className="shop-row-info-container">
                        <div className="shop-row-image-section">
                            {shop.shopImage && (
                                <SafeImage
                                    src={shop.shopImage}
                                    alt={shop.shopName}
                                    className="shop-row-logo-large"
                                    placeholderText="Shop"
                                />
                            )}
                        </div>

                        <div className="shop-row-meta-section">
                            {deliveryTimeDisplay && (
                                <div className="delivery-time-badge-large">
                                    <IconClock size={18} />
                                    <span className="pcu-font-800">
                                        {deliveryTimeDisplay}
                                    </span>
                                </div>
                            )}
                            {shop.rating > 0 && (
                                <div className="shop-row-rating-mini">
                                    <IconStar size={16} fill="#ffc107" />
                                    <span>{shop.rating.toFixed(1)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- PRODUCTS GRID ---------------- */}
            <Row gutter={[16, 16]}>
                {displayProducts.map((product) => (
                    <Col key={product._id} xs={12} sm={8} md={6} lg={4} xl={3}>
                        <ProductCard
                            product={{ ...product, shopId: shop, shopName: shop.shopName }}
                            formatETA={formatETA}
                        />
                    </Col>
                ))}
            </Row>

            {/* ---------------- CHECKOUT MODAL ---------------- */}
            <CheckoutModal
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                items={
                    checkoutProduct
                        ? [{ product: checkoutProduct, quantity: 1 }]
                        : []
                }
            />

        </div>
    );
};

export default ShopProductRow;
